import { useEffect, useRef, useState } from "react";
import "../../styles/AdminPanel/DiscountCodeCreate.css";
import "../../styles/AdminPanel/DiscountCodeList.css";
import { getAdminStores } from "../../api/admin/storeManagement";
import { getDiscount, updateDiscount } from "../../api/discount";
import { swalError, swalSuccess } from "../../utils/swalAlert";
import moment from "jalali-moment";
import toast, { Toaster } from "react-hot-toast";

import * as DatePickerModule from "react-multi-date-picker";

const DatePicker =
  DatePickerModule.default?.default || DatePickerModule.default;
import DateObject from "react-date-object";
import persian from "react-date-object/calendars/persian";
import persian_fa from "react-date-object/locales/persian_fa";
import gregorian from "react-date-object/calendars/gregorian";
import gregorian_en from "react-date-object/locales/gregorian_en";
const AVATAR_COLORS = [
  "var(--plum-900)",
  "var(--mauve-700)",
  "var(--rose-500)",
];
const FA_DIGITS = ["۰", "۱", "۲", "۳", "۴", "۵", "۶", "۷", "۸", "۹"];
const toFa = (n) => String(n).replace(/[0-9]/g, (d) => FA_DIGITS[d]);

function formatFaDate(dateStr) {
  return moment(dateStr, "YYYY-MM-DD").locale("fa").format("jD jMMMM jYYYY");
}
function isExpired(expiry) {
  if (!expiry) return false;
  return new Date(expiry + "T23:59:59") < new Date();
}

async function copyToClipboard(text) {
  try {
    if (navigator.clipboard && window.isSecureContext) {
      await navigator.clipboard.writeText(text);
      return true;
    }
  } catch (err) {
    console.error("خطا در کپی با Clipboard API", err);
  }
  // fallback برای مرورگرهای قدیمی‌تر یا حالت‌های غیر امن (non-HTTPS)
  try {
    const textarea = document.createElement("textarea");
    textarea.value = text;
    textarea.style.position = "fixed";
    textarea.style.opacity = "0";
    document.body.appendChild(textarea);
    textarea.focus();
    textarea.select();
    const ok = document.execCommand("copy");
    document.body.removeChild(textarea);
    return ok;
  } catch (err) {
    console.error("خطا در کپی با روش جایگزین", err);
    return false;
  }
}

// تست/toast مخصوص پیام‌های اطلاع‌رسانی (نه خطا، نه لودینگ)
function showInfoToast(message) {
  toast(message, {
    icon: (
      <svg viewBox="0 0 20 20" fill="none" width="18" height="18">
        <circle
          cx="10"
          cy="10"
          r="7.5"
          stroke="var(--plum-900)"
          strokeWidth="1.4"
        />
        <path
          d="M10 9.2V14"
          stroke="var(--plum-900)"
          strokeWidth="1.6"
          strokeLinecap="round"
        />
        <circle cx="10" cy="6.6" r="0.9" fill="var(--plum-900)" />
      </svg>
    ),
    style: {
      background: "#fff",
      color: "var(--plum-900)",
      fontWeight: 600,
      whiteSpace: "nowrap",
      minWidth: "fit-content",
    },
  });
}

const STORES_PER_PAGE = 9;

export default function DiscountCodeList() {
  const [discounts, setDiscounts] = useState([]);
  const [stores, setStores] = useState([]);
  // نسخه‌ی تجمعی همه فروشگاه‌هایی که تا الان از بک‌اند اومده‌اند (برای اینکه با تغییر جستجو
  // اطلاعات فروشگاه‌های قبلاً انتخاب‌شده گم نشه)
  const [storeCache, setStoreCache] = useState({});
  const [loading, setLoading] = useState(true);
  const [storesLoading, setStoresLoading] = useState(false);
  const [listSearch, setListSearch] = useState("");

  // ---- ویرایش فروشگاه‌های یک کد تخفیف ----
  const [expandedId, setExpandedId] = useState(null);
  const [editSelected, setEditSelected] = useState(new Set());
  // فروشگاه‌هایی که از این کد تخفیف استفاده کرده‌اند؛ دیگه نمیشه انتخابشون رو برداشت
  const [usedStoreIds, setUsedStoreIds] = useState(new Set());

  const [editSearch, setEditSearch] = useState("");
  const [editPage, setEditPage] = useState(1);
  const [saving, setSaving] = useState(false);
  const [editExpiry, setEditExpiry] = useState("");

  const discountSearchTimer = useRef(null);
  const isFirstDiscountSearch = useRef(true);
  const storeSearchTimer = useRef(null);
  const isFirstStoreSearch = useRef(true);

  // بک‌اند بر اساس متن جستجو فیلتر کنند و لیست فیلترشده رو برگردونند.
  const fetchDiscounts = async (search = "") => {
    setLoading(true);
    try {
      const discountData = await getDiscount(search);
      setDiscounts(Array.isArray(discountData.data) ? discountData.data : []);
    } catch (err) {
      console.error("خطا در دریافت لیست کدهای تخفیف", err);
      swalError({
        title: "خطا در دریافت اطلاعات",
        text:
          err?.detail ||
          err?.error ||
          "خطا در دریافت اطلاعات. لطفا دسترسی خود را بررسی کنید.",
      });
    } finally {
      setLoading(false);
    }
  };
  const fetchStores = async (search) => {
    setStoresLoading(true);
    try {
      const storeData = await getAdminStores(search);
      const list = Array.isArray(storeData) ? storeData : [];
      setStores(list);
      setStoreCache((prev) => {
        const next = { ...prev };
        list.forEach((s) => {
          next[s.store_id] = s;
        });
        return next;
      });
    } catch (err) {
      console.error("خطا در دریافت لیست فروشگاه‌ها", err);
      swalError({
        title: "خطا در دریافت فروشگاه‌ها",
        text: err?.detail || err?.error || "خطا در دریافت لیست فروشگاه‌ها.",
      });
    } finally {
      setStoresLoading(false);
    }
  };

  // بارگذاری اولیه‌ی لیست کدهای تخفیف
  useEffect(() => {
    fetchDiscounts();
    return () => {
      clearTimeout(discountSearchTimer.current);
      clearTimeout(storeSearchTimer.current);
    };
  }, []);

  // جستجوی کد تخفیف از سمت بک‌اند (با debounce)
  useEffect(() => {
    if (isFirstDiscountSearch.current) {
      isFirstDiscountSearch.current = false;
      return;
    }
    clearTimeout(discountSearchTimer.current);
    discountSearchTimer.current = setTimeout(() => {
      fetchDiscounts(listSearch);
    }, 400);
    return () => clearTimeout(discountSearchTimer.current);
  }, [listSearch]);

  // جستجوی فروشگاه از سمت بک‌اند، فقط وقتی پنل ویرایش بازه
  useEffect(() => {
    if (expandedId === null) {
      isFirstStoreSearch.current = true;
      return;
    }
    if (isFirstStoreSearch.current) {
      isFirstStoreSearch.current = false;
      fetchStores(editSearch);
      return;
    }
    clearTimeout(storeSearchTimer.current);
    storeSearchTimer.current = setTimeout(() => {
      fetchStores(editSearch);
      setEditPage(1);
    }, 400);
    return () => clearTimeout(storeSearchTimer.current);
  }, [editSearch, expandedId]);

  // console.log(discounts);

  const handleCopyCode = async (code) => {
    const ok = await copyToClipboard(code);
    if (ok) {
      toast.success(`کد «${code}» کپی شد`, {
        style: {
          fontWeight: 600,
          whiteSpace: "nowrap",
          minWidth: "fit-content",
        },
      });
    } else {
      swalError({
        title: "خطا در کپی",
        text: "کپی کردن کد تخفیف انجام نشد. لطفا دستی کپی کن.",
      });
    }
  };

  const openEdit = (discount) => {
    const discountStores = discount.stores || [];
    setExpandedId(discount.id);
    setEditSelected(new Set(discountStores.map((s) => s.store)));
    setUsedStoreIds(
      new Set(discountStores.filter((s) => s.used).map((s) => s.store)),
    );
    setEditSearch("");
    setEditPage(1);
    setEditExpiry(discount.expired_at || "");
  };

  const closeEdit = () => {
    setExpandedId(null);
    setEditSelected(new Set());
    setUsedStoreIds(new Set());
    setEditSearch("");
    setEditPage(1);
    setEditExpiry("");
  };

  const toggleEditStore = (id) => {
    // فروشگاهی که از کد تخفیف استفاده کرده رو نمیشه از حالت انتخاب خارج کرد
    if (usedStoreIds.has(id)) {
      showInfoToast(
        "این فروشگاه از تخفیف استفاده کرده، امکان ویرایش آن وجود ندارد",
      );
    }
    setEditSelected((prev) => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };

  const editTotalPages = Math.max(
    1,
    Math.ceil(stores.length / STORES_PER_PAGE),
  );
  const editSafePage = Math.min(editPage, editTotalPages);
  const editPagedStores = stores.slice(
    (editSafePage - 1) * STORES_PER_PAGE,
    editSafePage * STORES_PER_PAGE,
  );

  const editAllFilteredSelected =
    stores.length > 0 && stores.every((s) => editSelected.has(s.store_id));

  const toggleEditAll = () => {
    setEditSelected((prev) => {
      const next = new Set(prev);
      if (editAllFilteredSelected) {
        stores.forEach((s) => {
          // فروشگاه‌هایی که از کد استفاده کرده‌اند با انتخاب همه هم برداشته نمی‌شوند
          if (!usedStoreIds.has(s.store_id)) next.delete(s.store_id);
        });
        return next;
      }
      stores.forEach((s) => next.add(s.store_id));
      return next;
    });
  };

  const goToEditPage = (p) => {
    setEditPage(Math.min(Math.max(1, p), editTotalPages));
  };

  const handleSaveStores = async (discountId) => {
    setSaving(true);
    const storeIds = Array.from(editSelected);
    try {
      const res = await updateDiscount({
        id: discountId,
        expiry: editExpiry,
        storeIds,
      });
      const updated = res?.data?.data;
      setDiscounts((prev) =>
        prev.map((d) =>
          d.id === discountId
            ? updated
              ? { ...d, ...updated }
              : {
                  ...d,
                  expired_at: editExpiry,
                  stores: storeIds.map((id) => storeCache[id]).filter(Boolean),
                }
            : d,
        ),
      );
      // در صورت نیاز به هماهنگ‌سازی مجدد فیلد تاریخ با پاسخ سرور
      if (updated?.expired_at) setEditExpiry(updated.expired_at);
      swalSuccess?.({
        title: "ذخیره شد",
        text: "تغییرات این کد تخفیف بروزرسانی شد.",
      });
      // پنل باز می‌مونه تا کاربر بتونه ادامه بده و دوباره ویرایش کنه
    } catch (err) {
      console.error("خطا در بروزرسانی کد تخفیف", err);
      swalError({
        title: "خطا در بروزرسانی",
        text:
          err?.detail ||
          err?.error ||
          "بروزرسانی کد تخفیف انجام نشد. لطفا دوباره تلاش کن.",
      });
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="disc-page">
      <Toaster
        position="top-center"
        toastOptions={{
          duration: 3000,
          style: {
            fontFamily: "inherit",
            direction: "rtl",
            fontSize: "13.5px",
          },
        }}
      />
      <div className="disc-head">
        <h1>کدهای تخفیف</h1>
      </div>

      <section className="disc-card">
        <div className="disc-search-row">
          <svg className="disc-search-icon" viewBox="0 0 20 20" fill="none">
            <circle
              cx="9"
              cy="9"
              r="6.2"
              stroke="currentColor"
              strokeWidth="1.6"
            />
            <path
              d="M17 17L13.5 13.5"
              stroke="currentColor"
              strokeWidth="1.6"
              strokeLinecap="round"
            />
          </svg>
          <input
            type="text"
            className="disc-search-input"
            placeholder="جستجوی کد تخفیف..."
            value={listSearch}
            onChange={(e) => setListSearch(e.target.value)}
          />
          {listSearch && (
            <button
              type="button"
              className="disc-search-clear"
              onClick={() => setListSearch("")}
              aria-label="پاک کردن جستجو"
            >
              ×
            </button>
          )}
        </div>

        {loading && <div className="discl-loading">در حال بارگذاری...</div>}

        {!loading && discounts.length === 0 && (
          <div className="disc-empty-note">کد تخفیفی پیدا نشد</div>
        )}

        <div className="discl-list">
          {discounts.map((d) => {
            const expired = isExpired(d.expired_at);
            const isOpen = expandedId === d.id;
            return (
              <div className={`discl-row${isOpen ? " open" : ""}`} key={d.id}>
                <div className="discl-row-main">
                  <div className="discl-row-code">
                    <span
                      className="disc-code-val discl-code-val discl-code-copyable"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleCopyCode(d.code);
                      }}
                      role="button"
                      tabIndex={0}
                      title="برای کپی کلیک کن"
                      onKeyDown={(e) => {
                        if (e.key === "Enter" || e.key === " ") {
                          e.preventDefault();
                          handleCopyCode(d.code);
                        }
                      }}
                    >
                      {d.code}
                      <svg
                        className="discl-copy-icon"
                        viewBox="0 0 16 16"
                        fill="none"
                        width="13"
                        height="13"
                      >
                        <rect
                          x="5.5"
                          y="5.5"
                          width="8"
                          height="8"
                          rx="1.6"
                          stroke="currentColor"
                          strokeWidth="1.3"
                        />
                        <path
                          d="M3.5 10V3.6a1.1 1.1 0 0 1 1.1-1.1H10"
                          stroke="currentColor"
                          strokeWidth="1.3"
                        />
                      </svg>
                    </span>
                    <span
                      className={`discl-status${expired ? " expired" : ""}`}
                    >
                      {expired ? "منقضی شده" : "فعال"}
                    </span>
                  </div>

                  <div className="discl-row-meta">
                    <span>
                      <strong>{toFa(d.percentage)}٪</strong> تخفیف
                    </span>
                    <span>انقضا: {formatFaDate(d.expired_at)}</span>
                    <span>{toFa((d.stores || []).length)} فروشگاه</span>
                  </div>

                  <button
                    type="button"
                    className="disc-btn-ghost discl-toggle-btn"
                    onClick={() => (isOpen ? closeEdit() : openEdit(d))}
                    aria-label={isOpen ? "بستن" : "ویرایش فروشگاه‌ها"}
                    title={isOpen ? "بستن" : "ویرایش فروشگاه‌ها"}
                  >
                    <svg
                      className={`discl-toggle-arrow${isOpen ? " open" : ""}`}
                      viewBox="0 0 16 16"
                      fill="none"
                      width="16"
                      height="16"
                    >
                      <path
                        d="M4 6l4 4 4-4"
                        stroke="currentColor"
                        strokeWidth="1.8"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                  </button>
                </div>

                {isOpen && (
                  <div className="discl-edit-panel">
                    <div className="discl-expiry-field">
                      <label
                        className="discl-expiry-label"
                        htmlFor={`discl-expiry-${d.id}`}
                      >
                        تاریخ انقضا
                      </label>
                      <div className="discl-expiry-row">
                        <div className="discl-datepicker-wrap">
                          <DatePicker
                            id={`discl-expiry-${d.id}`}
                            calendar={persian}
                            locale={persian_fa}
                            calendarPosition="bottom-right"
                            fixedMainPosition={true}
                            value={
                              editExpiry
                                ? new DateObject({
                                    date: editExpiry,
                                    format: "YYYY-MM-DD",
                                    calendar: gregorian,
                                    locale: gregorian_en,
                                  }).convert(persian, persian_fa)
                                : null
                            }
                            onChange={(dateObject) => {
                              if (!dateObject) {
                                setEditExpiry("");
                                return;
                              }
                              const iso = dateObject
                                .convert(gregorian, gregorian_en)
                                .format("YYYY-MM-DD");
                              setEditExpiry(iso);
                            }}
                            format="YYYY/MM/DD"
                            editable={false}
                            inputClass={"disc-date-input"}
                            containerClassName="profile-jalali-container"
                            placeholder="انتخاب تاریخ انقضا"
                          />
                          <svg
                            className="discl-calendar-icon"
                            viewBox="0 0 20 20"
                            fill="none"
                          >
                            <rect
                              x="3"
                              y="4.5"
                              width="14"
                              height="12.5"
                              rx="2.2"
                              stroke="currentColor"
                              strokeWidth="1.4"
                            />
                            <path
                              d="M3 8.2H17"
                              stroke="currentColor"
                              strokeWidth="1.4"
                            />
                            <path
                              d="M6.5 3V5.6M13.5 3V5.6"
                              stroke="currentColor"
                              strokeWidth="1.4"
                              strokeLinecap="round"
                            />
                          </svg>
                        </div>
                        {editExpiry && (
                          <span className="discl-expiry-fa">
                            {formatFaDate(editExpiry)}
                          </span>
                        )}
                      </div>
                    </div>

                    <div className="disc-card-head">
                      <div className="disc-titles">
                        <h2>ویرایش فروشگاه‌ها</h2>
                        <span>
                          روی هر فروشگاه بزن تا برای کد «{d.code}» فعال یا
                          غیرفعال شود
                        </span>
                      </div>
                      <label className="disc-select-all">
                        <input
                          type="checkbox"
                          checked={editAllFilteredSelected}
                          ref={(el) =>
                            el &&
                            (el.indeterminate =
                              stores.some((s) =>
                                editSelected.has(s.store_id),
                              ) && !editAllFilteredSelected)
                          }
                          onChange={toggleEditAll}
                        />
                        انتخاب همه
                        <span className="disc-count">
                          {toFa(editSelected.size)} از {toFa(stores.length)}
                        </span>
                      </label>
                    </div>

                    <div className="disc-search-row">
                      <svg
                        className="disc-search-icon"
                        viewBox="0 0 20 20"
                        fill="none"
                      >
                        <circle
                          cx="9"
                          cy="9"
                          r="6.2"
                          stroke="currentColor"
                          strokeWidth="1.6"
                        />
                        <path
                          d="M17 17L13.5 13.5"
                          stroke="currentColor"
                          strokeWidth="1.6"
                          strokeLinecap="round"
                        />
                      </svg>
                      <input
                        type="text"
                        className="disc-search-input"
                        placeholder="جستجوی فروشگاه بر اساس نام"
                        value={editSearch}
                        onChange={(e) => {
                          setEditSearch(e.target.value);
                          setEditPage(1);
                        }}
                      />
                      {editSearch && (
                        <button
                          type="button"
                          className="disc-search-clear"
                          onClick={() => {
                            setEditSearch("");
                            setEditPage(1);
                          }}
                          aria-label="پاک کردن جستجو"
                        >
                          ×
                        </button>
                      )}
                    </div>

                    <div className="disc-store-grid">
                      {storesLoading && (
                        <div className="disc-empty-note">
                          در حال جستجوی فروشگاه‌ها...
                        </div>
                      )}
                      {!storesLoading && editPagedStores.length === 0 && (
                        <div className="disc-empty-note">
                          فروشگاهی با این مشخصات پیدا نشد
                        </div>
                      )}
                      {!storesLoading &&
                        editPagedStores.map((s, i) => {
                          const isUsed = usedStoreIds.has(s.store_id);
                          const isSelected =
                            editSelected.has(s.store_id) || isUsed;
                          return (
                            <div
                              key={s.store_id}
                              className={`disc-store${isSelected ? " selected" : ""}${isUsed ? " used" : ""}`}
                              onClick={() => toggleEditStore(s.store_id)}
                              style={
                                isUsed
                                  ? { cursor: "not-allowed", opacity: 0.85 }
                                  : undefined
                              }
                              title={
                                isUsed
                                  ? "این فروشگاه از تخفیف استفاده کرده، امکان ویرایش آن وجود ندارد"
                                  : undefined
                              }
                            >
                              <div
                                className="disc-avatar"
                                style={{
                                  background:
                                    AVATAR_COLORS[i % AVATAR_COLORS.length],
                                }}
                              >
                                {s.store_name.trim()[0]}
                              </div>
                              <div className="disc-meta">
                                <div className="disc-name">{s.store_name}</div>
                                <div className="disc-cat">{s.cat}</div>
                              </div>
                              <div className="disc-check">
                                {isUsed ? (
                                  <svg
                                    viewBox="0 0 12 12"
                                    fill="none"
                                    width="12"
                                    height="12"
                                  >
                                    <rect
                                      x="2.5"
                                      y="5.2"
                                      width="7"
                                      height="5"
                                      rx="1"
                                      stroke="white"
                                      strokeWidth="1"
                                    />
                                    <path
                                      d="M4 5.2V3.8a2 2 0 0 1 4 0v1.4"
                                      stroke="white"
                                      strokeWidth="1"
                                    />
                                  </svg>
                                ) : (
                                  <svg viewBox="0 0 12 12" fill="none">
                                    <path
                                      d="M2 6.2L4.8 9L10 3"
                                      stroke="white"
                                      strokeWidth="1.8"
                                      strokeLinecap="round"
                                      strokeLinejoin="round"
                                    />
                                  </svg>
                                )}
                              </div>
                            </div>
                          );
                        })}
                    </div>

                    {editTotalPages > 1 && (
                      <div className="disc-pagination">
                        <button
                          type="button"
                          className="disc-page-btn"
                          onClick={() => goToEditPage(editSafePage - 1)}
                          disabled={editSafePage === 1}
                        >
                          قبلی
                        </button>

                        <div className="disc-page-numbers">
                          {Array.from(
                            { length: editTotalPages },
                            (_, i) => i + 1,
                          ).map((p) => (
                            <button
                              key={p}
                              type="button"
                              className={`disc-page-num${p === editSafePage ? " active" : ""}`}
                              onClick={() => goToEditPage(p)}
                            >
                              {toFa(p)}
                            </button>
                          ))}
                        </div>

                        <button
                          type="button"
                          className="disc-page-btn"
                          onClick={() => goToEditPage(editSafePage + 1)}
                          disabled={editSafePage === editTotalPages}
                        >
                          بعدی
                        </button>
                      </div>
                    )}

                    <div className="discl-edit-actions">
                      <button
                        type="button"
                        className="disc-btn-ghost"
                        onClick={closeEdit}
                        disabled={saving}
                      >
                        انصراف
                      </button>
                      <button
                        type="button"
                        className="disc-btn-primary"
                        onClick={() => handleSaveStores(d.id)}
                        disabled={saving}
                      >
                        {saving ? "در حال ذخیره..." : "ذخیره"}
                      </button>
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </section>
    </div>
  );
}
